import {useNotificationStore} from '@/store/notification_store';
import {useNotificationItem} from '@/hooks/useNotificationItem';
import {
  MESSAGE_MENTION,
  NotificationDisplayText,
  NotificationDto,
  REFERRAL_EVENT,
  TASK_EVENT,
  BED_AVAILABILITY_EVENT,
  ADDITIONAL_EVENT
} from '@/api/dto/notification';
import ClientLink from '@/components/pages/notifications/links/ClientLink';
import MessageLink from '@/components/pages/notifications/links/MessageLink';
import TaskLink from "@/components/pages/notifications/links/TaskLink";
import ReferralLink from "@/components/pages/notifications/links/ReferralLink";
import CareJourneyLink from "@/components/pages/notifications/links/CareJourneyLink";
import {Roster} from "@/api/dto/dto";

type Props = {
  notification: NotificationDto;
  showSeparator?: boolean;
  roster?: Roster;
}

export default function NotificationContent({ notification, roster, showSeparator = false }: Props) {
  const setPopoverOpen = useNotificationStore((state) => state.setPopoverOpen);
  const { setNotificationViewed, isApiLoading } = useNotificationItem();

  const onClickNotificationMessage = () => {
    setPopoverOpen(false);
    if (!notification.viewed) {
      setNotificationViewed(notification.id, true);
    }
  };

  if (notification.notificationType === MESSAGE_MENTION) {
    return (
      <>
        Mentioned you in a <span>
          <MessageLink
            client={notification.client}
            message={notification.message}
            data-testid={`message-link-for-notification-${notification.id}`}
            disabled={isApiLoading}
            onClick={onClickNotificationMessage}
          />
        </span>
      </>
    );
  }

  if (notification.notificationType === TASK_EVENT) {
    return (
      <TaskLink
        metadata={notification.metadata}
        roster={roster}
        data-testid={`task-link-for-notification-${notification.id}`}
        onClick={onClickNotificationMessage}
      />
    );
  }

  if (notification.notificationType === ADDITIONAL_EVENT) {
    return (
      <>
        {'and merged to '}
        <span>
          <CareJourneyLink
            client={notification.client}
            metadata={notification.metadata}
            data-testid={`care-journey-link-for-notification-${notification.id}`}
            onClick={onClickNotificationMessage}
            roster={roster}
          />
        </span>
      </>
    );
  }

  if (notification.notificationType === REFERRAL_EVENT && notification.notificationSubtype === 'MESSAGE_ALERTING') {
    return (
      <>
        <span>
          <ClientLink
            client={notification.client}
            data-testid={`client-link-for-notification-${notification.id}`}
            onClick={onClickNotificationMessage}
            disabled={isApiLoading}
          />
        </span>
        {' had a '}
        <span>
          <ReferralLink
            message={notification.message}
            data-testid={`referral-link-for-notification-${notification.id}`}
            disabled={isApiLoading}
          />
        </span>
        {' from '}
        <span>{notification.message?.user.first_name} {notification.message?.user.last_name}</span>
      </>
    );
  }

  if (notification.notificationType === REFERRAL_EVENT && notification.notificationSubtype === 'EXCEED_REFERRAL') {
    return (
      <>
        <span>
          <ClientLink
            client={notification.client}
            data-testid={`client-link-for-notification-${notification.id}`}
            onClick={onClickNotificationMessage}
            disabled={isApiLoading}
          />
        </span>
        {' had a new referral event at '}
        {notification.group.name}
        {notification.unitName ? ` | ${notification.unitName}` : ''}
      </>
    );
  }
  if (notification.notificationType === REFERRAL_EVENT && notification.notificationSubtype === 'ESCALATE_REFERRAL') {
    return (
      <>
        {'Referral response time exceeded for '}
        <span>
          <ClientLink
            client={notification.client}
            data-testid={`client-link-for-notification-${notification.id}`}
            onClick={onClickNotificationMessage}
            disabled={isApiLoading}
          />
        </span>
        {' at '}
        {notification.group.name}
        {notification.unitName ? ` | ${notification.unitName}` : ''}
      </>
    );
  }
  if (notification.notificationType === REFERRAL_EVENT && notification.notificationSubtype === 'NEW_REFERRAL') {
    return (
      <>
        <span>
          <ClientLink
            client={notification.client}
            data-testid={`client-link-for-notification-${notification.id}`}
            onClick={onClickNotificationMessage}
            disabled={isApiLoading}
          />
        </span> had a new referral received for {notification.group.name}{showSeparator ? ' |' : ''}
      </>
    );
  }
  
  if (notification.notificationType === REFERRAL_EVENT
      && notification.notificationSubtype === 'REFERRAL_ANTICIPATED_DATE_OVERDUE') {
    return (
      <>
        <span>
          <ClientLink
            client={notification.client}
            data-testid={`client-link-for-notification-${notification.id}`}
            onClick={onClickNotificationMessage}
            disabled={isApiLoading}
          />
        </span>
        {' had a referral waitlist status that is overdue'}
        {showSeparator ? ' |' : ''}
      </>
    );
  }

  if (notification.notificationType === REFERRAL_EVENT
      && notification.notificationSubtype === 'REFERRAL_ANTICIPATED_DATE_OVERDUE') {
    return (
      <>
        <span>
          <ClientLink
            client={notification.client}
            data-testid={`client-link-for-notification-${notification.id}`}
            onClick={onClickNotificationMessage}
            disabled={isApiLoading}
          />
        </span>
        {' had a referral waitlist status that is overdue'}
        {showSeparator ? ' |' : ''}
      </>
    );
  }

  if (notification.notificationType === REFERRAL_EVENT
      && notification.notificationSubtype === 'REFERRAL_SCHEDULED_DATE_OVERDUE') {
    return (
      <>
        <span>
          <ClientLink
            client={notification.client}
            data-testid={`client-link-for-notification-${notification.id}`}
            onClick={onClickNotificationMessage}
            disabled={isApiLoading}
          />
        </span>
        {' had a accepted referral whose schedule date has passed'}
        {showSeparator ? ' |' : ''}
      </>
    );
  }

  if (notification.notificationType === REFERRAL_EVENT
      && (notification.notificationSubtype === 'REFERRAL_ACCEPTED'
      || notification.notificationSubtype === 'REFERRAL_DECLINED'
      || notification.notificationSubtype === 'REFERRAL_SHOW'
      || notification.notificationSubtype === 'REFERRAL_NO_SHOW'
      || notification.notificationSubtype === 'REFERRAL_ACCEPTED_ELSEWHERE'
      || notification.notificationSubtype === 'REFERRAL_PENDING'
      || notification.notificationSubtype === 'REFERRAL_WAITLISTED')) {
    return (
      <>
        <span>
          <ClientLink
            client={notification.client}
            data-testid={`client-link-for-notification-${notification.id}`}
            onClick={onClickNotificationMessage}
            disabled={isApiLoading}
          />
        </span> had a referral event at {notification.group.name}{showSeparator ? ' |' : ''}
      </>
    );
  }

  if (notification.notificationType === BED_AVAILABILITY_EVENT) {
    const groupAndUnit = `${notification.group.name}${notification.unitName ? ` | ${notification.unitName}` : ''}`;

    if (notification.notificationSubtype === 'BED_AVAILABILITY_NOT_UPDATED') {
      return <>Update bed availability for {groupAndUnit}</>;
    }
    if (notification.notificationSubtype === 'BED_AVAILABILITY_ESCALATION') {
      return <>Bed availability update time exceeded for {groupAndUnit}</>;
    }
    if (notification.notificationSubtype === 'OUTPATIENT_APPOINTMENT_NOT_UPDATED') {
      return <>Update next available appointment for {groupAndUnit}</>;
    }
    if (notification.notificationSubtype === 'OUTPATIENT_APPOINTMENT_ESCALATION') {
      return <>Next available appointment update time exceeded for {groupAndUnit}</>;
    }

    return <>{groupAndUnit}</>;
  }

  const eventAndCategoryText = NotificationDisplayText[
    notification.notificationType][notification.notificationSubtype]
    ? NotificationDisplayText[notification.notificationType][notification.notificationSubtype](notification)
    : notification.notificationSubtype;

  return (
    <>
      <span>
        <ClientLink
          client={notification.client}
          data-testid={`client-link-for-notification-${notification.id}`}
          onClick={onClickNotificationMessage}
          disabled={isApiLoading}
        />
      </span> had a {eventAndCategoryText.toLowerCase()} event for {notification.group.name}
    </>
  );
}
